import { FetchAPI } from '../Main/fetchAPI';
import { CLIENT } from '../constants';

const RECURRING_SCHEDULES = `${CLIENT}/recurring-schedules`;

export interface RecurringScheduleTimeSlot {
	dayOfWeek: number;
	endTimeMinutes: number;
	id?: string | null;
	startTimeMinutes: number;
}

export interface CreateRecurringSchedulePayload {
	acknowledgeTotalHoursWarning?: boolean;
	clientId: string;
	endDate: string;
	frequency: number;
	providerId: string;
	sessionTypeId: number;
	startDate: string;
	step: number;
	timeSlots: RecurringScheduleTimeSlot[];
	weekStartsOn: number;
}

export interface UpdateRecurringSchedulePayload {
	startDate?: string | null;
	acknowledgeTotalHoursWarning?: boolean;
	endDate: string | null;
	timeSlots: RecurringScheduleTimeSlot[];
}

export interface RecurringScheduleViewModel {
	branchId: string | null;
	daysOfWeek: number[];
	effectiveEndDate?: string | null;
	endDate: string | null;
	endDateLimit?: string | null;
	frequency: number;
	id: string;
	providerId: string;
	providerName: string;
	sessionTypeId: number;
	sessionTypeName: string;
	startDate: string;
	step: number;
	timeSlots: RecurringScheduleTimeSlot[];
	weekStartsOn: number;
}

export interface AuthorizationDetailScheduleMetrics {
	authorizedHoursPerWeek: number;
	availableHoursPerWeek: number;
	isForRbts: boolean;
	// True when this detail is enforced/displayed as a weekly cap. False for Entire Period details,
	// which are enforced as a period-total budget and described by the *TotalHours fields instead.
	isWeekly: boolean;
	scheduledHoursPerWeek: number;
	sessionTypeName: string;
	authorizedTotalHours: number;
	availableTotalHours: number;
	scheduledTotalHours: number;
}

export interface ClientScheduleOverviewViewModel {
	activeScheduleCount: number;
	adherenceActualSessionsToDate: number;
	adherenceScheduledSessionsThisMonth: number;
	adherenceScheduledSessionsToDate: number;
	authorizationDetails: AuthorizationDetailScheduleMetrics[];
	authorizationEndDate: string | null;
	authorizedHoursPerWeek: number;
	cancellationsLast30Days: number;
	clientId: string;
	clientName: string;
	dateOfBirth: string;
	isElc: boolean;
	hoursScheduledThisMonth: number;
	hoursScheduledToDateThisMonth: number;
	hoursUtilizedThisMonth: number;
	scheduleAdherencePercent: number;
	scheduledHoursPerWeek: number;
	schedules: RecurringScheduleViewModel[];
}

export interface GetOverviewParams {
	branchId?: string;
	clientId?: string;
	includeEnded?: boolean;
	providerId?: string;
}

export interface RecurringScheduleProviderViewModel {
	fullName: string;
	id: string;
}

export interface RecurringScheduleClientViewModel {
	dateOfBirth: string;
	fullName: string;
	id: string;
	isElc: boolean;
}

export interface GetScheduledProvidersParams {
	includeEnded?: boolean;
	page?: string;
	pageSize?: string;
	searchString?: string;
}

export const RecurringSchedule = {
	getOverview: (params?: GetOverviewParams) => {
		const query = new URLSearchParams();
		if (params?.clientId) query.set('clientId', params.clientId);
		if (params?.branchId) query.set('branchId', params.branchId);
		if (params?.includeEnded) query.set('includeEnded', 'true');
		if (params?.providerId) query.set('providerId', params.providerId);
		const qs = query.toString();

		return FetchAPI.getJson({ urlStr: `${RECURRING_SCHEDULES}/overview${qs ? `?${qs}` : ''}` });
	},
	getScheduledProviders: (params?: GetScheduledProvidersParams) => {
		const query = new URLSearchParams();
		if (params?.searchString) query.set('searchString', params.searchString);
		if (params?.includeEnded) query.set('includeEnded', 'true');
		if (params?.page) query.set('page', params.page);
		if (params?.pageSize) query.set('pageSize', params.pageSize);
		const qs = query.toString();

		return FetchAPI.getJson({ urlStr: `${RECURRING_SCHEDULES}/providers${qs ? `?${qs}` : ''}` });
	},
	getScheduledClients: (params?: GetScheduledProvidersParams) => {
		const query = new URLSearchParams();
		if (params?.searchString) query.set('searchString', params.searchString);
		if (params?.includeEnded) query.set('includeEnded', 'true');
		if (params?.page) query.set('page', params.page);
		if (params?.pageSize) query.set('pageSize', params.pageSize);
		const qs = query.toString();

		return FetchAPI.getJson({ urlStr: `${RECURRING_SCHEDULES}/clients${qs ? `?${qs}` : ''}` });
	},
	getAuthorizationBreakdown: (clientId: string) =>
		FetchAPI.getJson({ urlStr: `${RECURRING_SCHEDULES}/authorization-breakdown/${clientId}` }),
	getActiveAuthorizations: (clientId: string) =>
		FetchAPI.getJson({ urlStr: `${RECURRING_SCHEDULES}/clients/${clientId}/active-authorizations` }),
	// Session types selectable for the chosen provider, applying the same role + Meet & Greet
	// filtering as session creation, gated by the schedules permission (not /sessions/types).
	getProviderSessionTypes: ({ clientId, providerId, date }: { clientId: string; providerId: string; date?: string }) => {
		const query = new URLSearchParams();
		if (date) query.set('date', date);
		const qs = query.toString();

		return FetchAPI.getJson({
			urlStr: `${RECURRING_SCHEDULES}/clients/${clientId}/providers/${providerId}/session-types${qs ? `?${qs}` : ''}`,
		});
	},
	getByClient: (clientId: string) =>
		FetchAPI.getJson({ urlStr: `${RECURRING_SCHEDULES}/client/${clientId}` }),
	getById: (id: string) => FetchAPI.getJson({ urlStr: `${RECURRING_SCHEDULES}/${id}` }),
	create: (data: CreateRecurringSchedulePayload[]) =>
		FetchAPI.postJson({ urlStr: RECURRING_SCHEDULES, data }),
	update: (id: string, data: UpdateRecurringSchedulePayload) =>
		FetchAPI.putJson({ urlStr: `${RECURRING_SCHEDULES}/${id}`, data }),
	updateEndDate: (id: string, endDate: string | null) =>
		FetchAPI.putJson({ urlStr: `${RECURRING_SCHEDULES}/${id}`, data: { endDate } }),
	delete: (id: string) => FetchAPI.deleteJson({ urlStr: `${RECURRING_SCHEDULES}/${id}` }),
};
